import {
  Archive,
  CheckCircle2,
  CircleDollarSign,
  Clock,
  FileEdit,
  Lock,
  Truck,
  Wallet,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import { type supplyChainBooking } from "../Booking";
import { Badge } from "@/components/ui/badge";
import { type ResultOf } from "@/graphql";
import { cn } from "@/lib/utils/common";
import { formatEnumLabel } from "@/lib/utils/formatting";

export type BookingStatus = NonNullable<ResultOf<typeof supplyChainBooking>["supplyChainBooking"]>["status"];

interface StatusConfig {
  icon: LucideIcon;
  className: string;
}

const statusConfig = {
  PLANNED: {
    icon: Clock,
    className: "bg-blue-100 text-blue-800",
  },
  DRAFT: {
    icon: FileEdit,
    className: "bg-gray-200 text-gray-800",
  },
  BOOKED: {
    icon: CheckCircle2,
    className: "bg-amber-100 text-amber-800",
  },
  SHIPPED: {
    icon: Truck,
    className: "bg-purple-100 text-purple-800",
  },
  AWAITING_PAYMENT: {
    icon: Wallet,
    className: "bg-breedr-yellow-300/30 text-breedr-yellow-900",
  },
  PAID: {
    icon: CircleDollarSign,
    className: "bg-green-100 text-green-800",
  },
  CANCELLED: {
    icon: XCircle,
    className: "bg-red-200/30 text-red-800",
  },
  ARCHIVED: {
    icon: Archive,
    className: "bg-amber-800/30 text-amber-900",
  },
  CLOSED: {
    icon: Lock,
    className: "bg-slate-700 text-white",
  },
} satisfies Record<BookingStatus, StatusConfig>;

interface BookingStatusBadgeProps {
  status: BookingStatus;
  className?: string;
}

export function BookingStatusBadge({ status, className }: BookingStatusBadgeProps) {
  const config = statusConfig[status];
  const Icon = config.icon;

  return (
    <Badge
      variant="secondary"
      className={cn("gap-2 h-8 px-4 font-medium border-none rounded-full", config.className, className)}
    >
      <Icon className="h-3.5 w-3.5" />
      {formatEnumLabel(status)}
    </Badge>
  );
}
